import React from "react";

import { intersection } from "lodash/fp";

import Collapse from "@mui/material/Collapse";

import Setting from "@app/analytics/account/components/Setting";
import SettingContent from "@app/analytics/account/components/SettingContent";
import SettingWithToggle from "@app/analytics/account/components/SettingWithToggle";
import { getSettingDisabledOpts } from "@app/analytics/account/UserPreferences/utils";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import type { CalendarPermissionLevel } from "@app/analytics/types";
import ROUTES from "@app/common/routes";
import type { User, UserSettings } from "@app/common/types";

import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";
import { Column } from "@rui/Stack";

import MeetingRoleSelect from "./MeetingRoleSelect";
import MeetingTypeSelect from "./MeetingTypeSelect";

const ALL_CALENDAR_PERMISSION_LEVELS: CalendarPermissionLevel[] = [
  "all",
  "hosting_only",
  "invite_only",
];

const getEnabledCalendarPermissionLevels = (
  editOptions?: CalendarPermissionLevel[]
) => {
  if (!editOptions) return ALL_CALENDAR_PERMISSION_LEVELS;
  return intersection(ALL_CALENDAR_PERMISSION_LEVELS, editOptions);
};

const JoinPreferenceSettings = ({
  onChange,
  user,
}: {
  onChange: (settings: Partial<UserSettings>) => void;
  user: User;
}) => {
  const { hasConnectedCalendarProviders } = useConnectedProviders();

  const calenderPermissionLevelOptions = getEnabledCalendarPermissionLevels(
    user.editOptions?.calendarPermissionLevel
  );

  const autoJoinDisabledOpts = getSettingDisabledOpts(
    calenderPermissionLevelOptions.length > 1
  );

  const handleToggleAutoJoin = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    onChange({
      calendarPermissionLevel: checked
        ? calenderPermissionLevelOptions[0]
        : "invite_only",
    });
  };

  const userCalendarPermissionLevel =
    user.calendarPermissionLevel ?? "invite_only";

  const willAutoJoin =
    hasConnectedCalendarProviders &&
    userCalendarPermissionLevel !== "invite_only";

  return (
    <Setting title="Join Preferences">
      <SettingContent spacing={1}>
        <SettingWithToggle
          title="Auto-join meetings"
          description="Allow Read Assistant to automatically join meetings and record to generate a recap."
          disabled={
            autoJoinDisabledOpts.disabled || !hasConnectedCalendarProviders
          }
          disabledTooltip={
            autoJoinDisabledOpts.tooltip ??
            "Connect a calendar provider to alter this setting"
          }
          onChange={handleToggleAutoJoin}
          checked={willAutoJoin}
        />
        <Collapse in={!willAutoJoin} unmountOnExit>
          <InfoPanel severity="info">
            Add or remove Read from your meetings on your{" "}
            <Link underline to={ROUTES.CALENDAR}>
              Calendar
            </Link>{" "}
            page.
          </InfoPanel>
        </Collapse>
        <Collapse in={willAutoJoin} unmountOnExit>
          <Column spacing={1}>
            <MeetingRoleSelect
              onChange={onChange}
              options={calenderPermissionLevelOptions}
              selected={userCalendarPermissionLevel}
            />
            <MeetingTypeSelect user={user} onChange={onChange} />
          </Column>
        </Collapse>
      </SettingContent>
    </Setting>
  );
};

export default JoinPreferenceSettings;
